import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import FocusTrap from 'focus-trap-react';
import { Button } from './Button.js';
import { FiltersTrigger } from './FiltersTrigger.js';
import { cn } from '../lib/utils.js';
import { focusRing } from '../lib/focus.js';
import { FiltersGroup } from './FiltersGroup.js';
import { portalHost } from '../lib/portalHost.js';
import { useDismiss } from '../lib/useDismiss.js';
import {
  placeFiltersPopover,
  type FiltersPopoverPosition,
} from '../lib/filtersPopoverPlacement.js';

/* ===========================================================================
 * FiltersPopover — the toolbar's second slot, and the retirement of a pattern.
 *
 * §2 of docs/design-rules.md is explicit twice over: "Filters always collapse
 * into a popover with a count badge — never a row of stacked label+control
 * pairs. That was the old pattern and it is retired." §6 states the general form
 * of the same rule: do not build a second control language; every filter goes in
 * the one toolbar.
 *
 * That is a stronger claim than it looks, so it is worth saying why it is right
 * rather than treating it as taste. A row of stacked label+control pairs has
 * three properties a popover does not:
 *
 *   - it grows the toolbar with the number of filters, so the bar is a different
 *     height on every screen and the table below it starts in a different place;
 *   - it shows every filter at full weight whether or not it is doing anything,
 *     so a screen with nine filters and none applied looks as busy as one with
 *     nine applied — and the reader cannot tell which case they are in;
 *   - it has nowhere to put the ANSWER to "what is currently narrowing this
 *     list", which is the only question a reader actually has.
 *
 * The count badge is that answer, and it is why the trigger carries one. "2"
 * beside the word Filters is a whole sentence: two things are hiding rows from
 * you, and here is where they are. A count of zero draws NO badge — a badge
 * reading "0" is a control claiming to be doing something.
 *
 * ---------------------------------------------------------------------------
 * THE PANEL IS A `dialog`, TRAPPED AND ESCAPABLE.
 *
 * A popover holding chips, selects, switches and an Apply button is a small
 * form, and a small form the Tab key can walk out of — leaving focus behind a
 * panel that has no overlay to click, with no way back — is what makes keyboard
 * users abandon a filter UI. `packages/ui` already depends on `focus-trap-react`
 * and `Dialog.tsx` already settled the option set; both are reused rather than
 * re-derived.
 *
 * Three differences from `Dialog`, all because this is ANCHORED and not modal:
 *
 *   - `allowOutsideClick`, plus our own outside-pointerdown handler. A modal
 *     eats outside clicks; a popover is dismissed BY them, because a reader who
 *     clicks the table has answered the question.
 *   - no body-scroll lock and no scrim. The page behind a filter panel is the
 *     thing being filtered — dimming it defeats the point.
 *   - focus returns to the TRIGGER on Escape, which makes Escape a round trip
 *     rather than a way to lose your place. It deliberately does NOT return on
 *     an outside click: pulling the caret back to the trigger after someone
 *     clicked a row would take focus off the thing they just chose.
 *
 * ---------------------------------------------------------------------------
 * ENTRANCE. `--ease-entrance`, and no new keyframe.
 *
 * The token layer's one entrance keyframe is `dialog-in`, which travels a full
 * height up from the bottom edge — right for a sheet, wrong for a panel hanging
 * 8px under a button. Rather than add a second keyframe to a preset three apps
 * import, the entrance is a TRANSITION flipped on the frame after mount: the
 * panel renders at `opacity-0 -translate-y-1` and lands on the next frame. Same
 * curve, same duration band, nothing added to the shared file.
 *
 * `-translate-y-1` is on the BLOCK axis, so it needs no mirroring — the panel
 * arrives from above in both writing directions.
 *
 * ---------------------------------------------------------------------------
 * WHY THE GROUP HEADINGS ARE NOT THE MOCK'S 11px CAPS. All four toolbar mocks
 * set them at 11px / 600 / uppercase / +.6px. §1 and §6 of the rules file ban
 * exactly that for a label, on the grounds that at that size caps read as
 * texture — and a filter group heading is a label for the controls under it.
 * `text-label` (12.5px, sentence case) is what the rules prescribe, and §0 of
 * docs/design-system-migration.md already settled the identical disagreement for
 * table headers in the rules file's favour. The mock's 11px caps survive only on
 * card-internal eyebrow captions, which this is not.
 *
 * RTL: `start-0`, `ms-auto`, logical padding. No physical `left`/`right`.
 * ======================================================================== */

export interface FiltersPopoverProps {
  /**
   * The panel's heading — "Filter employees", "Filter the queue". Also the
   * panel's accessible name, so the two cannot disagree.
   */
  heading: string;
  /** The trigger's label. Defaults to "Filters"; pass a translated string. */
  label: string;
  /**
   * How many filters are currently applied. `0` or `undefined` draws no badge.
   */
  count?: number;
  /**
   * What the count means for a reader who never sees the badge — "2 filters
   * applied". Defaults to the bare number, which is better than nothing and
   * worse than a sentence, so pass one.
   */
  countLabel?: string;
  /** The filter controls. Group them with `FiltersPopover.Group`. */
  children: ReactNode;
  /** Clears every filter. The link is omitted entirely when not passed. */
  onReset?: () => void;
  resetLabel: string;
  /**
   * Commits and closes. Omit it when the filters apply as they are changed — in
   * which case the panel is dismissed by Escape, an outside click or the
   * trigger, and a full-width Apply would be a fourth way to do nothing. (Same
   * rule `Dialog.tsx` states for a form dialog's Cancel.)
   */
  onApply?: () => void;
  applyLabel?: string;
  /** Controlled open state. Leave both undefined to let the component own it. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * ICON-ONLY TRIGGER — opt-in, and never the default.
   *
   * A rail floating over a map is ~272px wide and its job is to LIST people; a
   * full-width "Filters" row under its search box spends one of the few rows it
   * has on a word. Compact drops the word and the chevron and keeps the control
   * square, so it sits BESIDE the search field instead of under it.
   *
   * Two things do not change, because they are what makes the control honest:
   *
   *   - THE COUNT BADGE STAYS. An icon that hides how many filters are on
   *     leaves a narrowed list looking unnarrowed — §3, not cosmetics. The badge
   *     is the only thing on screen saying the rows are a subset.
   *   - THE NAME STAYS. `label` becomes `aria-label` and `title`, so the control
   *     still announces "Filters" and still names itself on hover.
   *
   * Opt-in, so a toolbar with room for the word keeps it: an icon is a guess, a
   * word is not, and only a screen that has measured its own space should trade
   * one for the other.
   */
  compact?: boolean;
  /** Extra classes for the anchor, not the panel. */
  className?: string;
}

export function FiltersPopover({
  heading,
  label,
  count,
  countLabel,
  children,
  onReset,
  resetLabel,
  onApply,
  applyLabel = 'Apply filters',
  open: controlledOpen,
  onOpenChange,
  compact = false,
  className,
}: FiltersPopoverProps): JSX.Element {
  const panelId = useId();
  const headingId = useId();
  const anchorRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [entered, setEntered] = useState(false);
  const restoreFocus = useRef(false);
  const open = controlledOpen ?? uncontrolledOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (controlledOpen === undefined) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [controlledOpen, onOpenChange]
  );

  /**
   * Close AND put the caret back on the trigger — the keyboard round trip that
   * Escape and Apply both take. An outside click calls `setOpen(false)` directly
   * instead, because pulling focus back to the trigger would take it off the row
   * the reader just clicked.
   *
   * THE RESTORE CANNOT HAPPEN HERE, and that is the whole reason for the flag.
   * `setOpen(false)` does not unmount anything synchronously, so a `focus()` on
   * this line runs while the panel is still mounted and the trap is still
   * active — the trap immediately yanks focus back inside, and then the panel
   * unmounts with `returnFocusOnDeactivate: false` and leaves focus on `<body>`.
   * Measured exactly that way before the flag was added. So the intent is
   * recorded and the effect below acts on it once the DOM has caught up.
   *
   * `returnFocusOnDeactivate` cannot do this job either: it fires on BOTH exits,
   * including the outside click that must not move focus.
   */
  const dismiss = useCallback(() => {
    restoreFocus.current = true;
    setOpen(false);
  }, [setOpen]);

  /**
   * THE PANEL IS PLACED IN THE VIEWPORT, NOT IN ITS PARENT.
   *
   * It used to be `absolute top-[2.875rem] start-0` inside the anchor, which is
   * correct until an ancestor has `overflow-hidden` — and then the panel is
   * clipped by a box that knows nothing about it. That is not hypothetical:
   * the operations rail is an `overflow-hidden rounded-panel` capped at
   * `max-h-72` when it docks, so a 316px panel hanging 46px below its trigger
   * is cut off inside it, and the screen there had to reach for a native
   * `<select>` instead of the control §2 prescribes.
   *
   * §4 already names this family of bug for Leaflet — a child that escapes, or
   * fails to escape, a stacking or clipping context its parent never declared
   * for it. The answer there was to stop the map's numbers leaking OUT; the
   * answer here is the mirror of it: take the panel out of the flow entirely,
   * so no ancestor's `overflow` can reach it.
   *
   * `position: fixed` in a portal on `portalHost()` — `document.body`, or the
   * element that is fullscreen when one is, because the Fullscreen API paints
   * only that element's subtree and a panel hanging off `body` is then not
   * drawn at all (see `lib/portalHost.ts`; `/operations` is the caller). Placed
   * from the trigger's own rect each time it opens and on any scroll or resize
   * while it is open.
   * The listeners are capture-phase and passive: a popover anchored to a
   * control inside a scrolling table has to travel with that control, and
   * `true` catches scrolls on ancestors rather than only on the window.
   */
  const [pos, setPos] = useState<FiltersPopoverPosition | null>(null);
  /** The panel's width before anything capped it — see `placeFiltersPopover`. */
  const naturalWidth = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      naturalWidth.current = null;
      return;
    }
    function place(): void {
      const trigger = triggerRef.current;
      const panel = panelRef.current;
      if (!trigger || !panel) return;
      naturalWidth.current ??= panel.getBoundingClientRect().width;
      setPos(
        placeFiltersPopover({
          trigger: trigger.getBoundingClientRect(),
          naturalHeight: panel.scrollHeight,
          naturalWidth: naturalWidth.current,
          rtl: getComputedStyle(trigger).direction === 'rtl',
          viewportWidth: window.innerWidth,
          viewportHeight: window.innerHeight,
        })
      );
    }
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  useEffect(() => {
    if (open || !restoreFocus.current) return;
    restoreFocus.current = false;
    triggerRef.current?.focus();
  }, [open]);

  // The entrance flip. Keyed on `open` so it re-arms every time the panel
  // mounts, and reset on close so the next open starts from `opacity-0` rather
  // than appearing fully formed.
  useEffect(() => {
    if (!open) {
      setEntered(false);
      return;
    }
    const frame = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(frame);
  }, [open]);

  /* Escape is the keyboard round trip (`dismiss` puts the caret back on the
     trigger); an outside press only closes. The panel is PORTALLED, so it is
     not inside the anchor, and its own children may portal one level further
     out (`Combobox`'s listbox) — `useDismiss` treats both, and any
     `[data-portal-surface]`, as inside. */
  useDismiss(open, [anchorRef, panelRef], (how) => (how === 'escape' ? dismiss() : setOpen(false)));

  return (
    // `relative` anchors the panel, and the anchor is also what the outside-click
    // test measures against — so a click on the TRIGGER is not "outside" and
    // cannot reopen what it just closed.
    <div ref={anchorRef} className={cn('relative flex-none', className)}>
      <FiltersTrigger
        ref={triggerRef}
        label={label}
        count={count}
        countLabel={countLabel}
        open={open}
        onToggle={() => (open ? dismiss() : setOpen(true))}
        panelId={open ? panelId : undefined}
        compact={compact}
      />

      {open &&
        createPortal(
          <FocusTrap
            focusTrapOptions={{
            // Escape is handled above, so the trap must not also act on it —
            // its own deactivate path would skip the focus restore.
            escapeDeactivates: false,
            // The outside click is handled above too. The trap has to let it
            // through rather than swallowing it, or the first click outside the
            // panel does nothing at all.
            clickOutsideDeactivates: false,
            allowOutsideClick: true,
            // Focus starts on the panel (hence `tabIndex={-1}`), never its first
            // field: a type-ahead opens its list on focus. The fallback covers a
            // panel whose controls are still loading — a trap with none throws.
            fallbackFocus: () => panelRef.current ?? document.body,
            initialFocus: () => panelRef.current ?? false,
            returnFocusOnDeactivate: false,
          }}
        >
          <div
            ref={panelRef}
            id={panelId}
            role="dialog"
            aria-labelledby={headingId}
            tabIndex={-1}
            /* Placed by the layout effect. Until it has measured, the panel is
               parked off-screen rather than flashed at 0,0 — `useLayoutEffect`
               runs before paint, so this is what the browser draws only if the
               trigger has somehow gone away. */
            style={{
              top: pos?.top ?? -9999,
              left: pos?.left ?? -9999,
              // Set only once measured: an unbounded first paint is what lets
              // `scrollHeight` report the panel's natural height above.
              ...(pos
                ? { maxHeight: pos.maxHeight, maxWidth: pos.maxWidth, overflowY: 'auto' as const }
                : {}),
            }}
            className={cn(
              // Placed by the effect above, in the viewport rather than in the
              // anchor. `z-40` still, but now against `document.body` — there
              // is no ancestor left to be trapped under.
              'fixed z-40',
              // 316px, the handoff's width, capped at `--popover-max` (352px) —
              // the width role the token layer documents for "something anchored
              // to the control that opened it".
              'w-[19.75rem] max-w-popover',
              // `border-subtle`, not `border`: the panel is a CONTAINER, and the
              // handoff draws a container's edge one step lighter than a
              // control's — `1px solid #E7E3DB` here (Hadir
              // Operations.dc.html:246) against the #DDD8CE on the trigger above
              // it. See the Boundaries note in apps/web/src/index.css.
              'rounded-toolbar border border-border-subtle bg-surface p-3.5 shadow-popover',
              'focus-visible:outline-none',
              // The entrance — see the header note on why a transition and not a
              // keyframe. `motion-reduce` drops the travel, not the panel.
              'transition-[opacity,transform] duration-fast ease-entrance motion-reduce:transition-none',
              entered ? 'translate-y-0 opacity-100' : '-translate-y-1 opacity-0'
            )}
          >
            <div className="mb-component flex items-center gap-element">
              {/* NOT `text-h3` (17px). A 17px heading inside a 316px panel
                  hanging off a button reads as a card title — it claims more of
                  the page than a popover has. The handoff sets it at
                  `font-family: 'Archivo'; font-weight: 600; font-size: 13.5px`
                  (Hadir Operations.dc.html:247): the display face and the
                  heading weight at the body step, which is what makes it a
                  LABEL FOR THE PANEL rather than a section of the page.
                  `font-display` has to be said here because the family rides on
                  the five display type steps, and `text-copy` is not one. */}
              <div id={headingId} className="font-display text-copy font-semibold">
                {heading}
              </div>
              {onReset && (
                <button
                  type="button"
                  onClick={onReset}
                  className={cn(
                    // A text link, NOT a `<Button variant="ghost">`. Reset is a
                    // way back; a Button — even ghost — brings a control's
                    // padding, its press scale and its state layer, which would
                    // put "undo my filtering" at the same weight as Apply. Two
                    // controls of equal weight for two decisions of very unequal
                    // importance is the rule `Dialog.tsx` states for footers.
                    'ms-auto rounded-2xs px-1 py-0.5 text-label text-primary',
                    'transition-colors duration-fast ease-standard hover:text-primary-hover',
                    focusRing
                  )}
                >
                  {resetLabel}
                </button>
              )}
            </div>

            {/* Groups are separate questions, so the distance between them is
                the component step (16px) while the controls inside one are at
                the element step (8px). Collapse the two and the panel reads as
                one long list of chips with headings sprinkled through it. */}
            <div className="flex flex-col gap-component">{children}</div>

            {onApply && (
              // The real `Button` primitive, full width. FULL WIDTH is the point
              // and is the one place in the system a button is: the panel is
              // 316px of questions and this is its single answer, so an
              // inline-sized button in the corner would read as optional.
              //
              // Using `Button` rather than a hand-rolled fill is what keeps the
              // press ramp, the focus ring, the coarse-pointer floor and the
              // disabled treatment identical to every other primary action —
              // this component has no business owning a second version of them.
              <Button
                type="button"
                onClick={() => {
                  onApply();
                  // Apply is a commit, so it closes — and it uses the same
                  // round trip Escape does, putting the caret back on the
                  // trigger. A keyboard reader who applies a filter should end
                  // up on the control that now reads "Filters 3", not adrift on
                  // a button that no longer exists.
                  dismiss();
                }}
                className="mt-component w-full"
              >
                {applyLabel}
              </Button>
            )}
          </div>
        </FocusTrap>,
          portalHost()
        )}
    </div>
  );
}

FiltersPopover.Group = FiltersGroup;
